/**
 * YAML text helpers: quoted scalars and block scalars.
 *
 * Both fold line breaks the way YAML defines: inside a quoted or plain scalar, one line
 * break becomes a space and each blank line becomes a line feed; a block scalar keeps
 * its breaks (literal) or folds the ones between ordinary lines (folded).
 */

export type Fail = (
  code: string,
  offset: number,
  params?: Record<string, string | number>
) => never;

const RE_HEX_ESCAPE = /^[0-9a-fA-F]+$/;

const SIMPLE_ESCAPES: Readonly<Record<string, string>> = {
  "0": "\0",
  a: "\u0007",
  b: "\b",
  t: "\t",
  "\t": "\t",
  n: "\n",
  v: "\u000B",
  f: "\f",
  r: "\r",
  e: "\u001B",
  " ": " ",
  '"': '"',
  "/": "/",
  "\\": "\\",
  N: "\u0085",
  _: "\u{A0}",
  L: "\u{2028}",
  P: "\u{2029}",
};

const HEX_LENGTHS: Readonly<Record<string, number>> = { x: 2, u: 4, U: 8 };

export interface Quoted {
  value: string;
  /** The index just past the closing quote. */
  end: number;
}

function isSpace(ch: string | undefined): boolean {
  return ch === " " || ch === "\t";
}

function skipInlineSpace(s: string, from: number): number {
  let i = from;
  while (i < s.length && (s[i] === " " || s[i] === "\t")) {
    i++;
  }
  return i;
}

/**
 * Fold a line break found at `i` (s[i] is "\n"): drop the spaces before it, count the
 * blank lines after it, and return the text to add and where to resume.
 */
function foldBreak(
  s: string,
  i: number,
  out: string
): { add: string; trim: number; next: number } {
  let trailing = 0;
  while (trailing < out.length && isSpace(out.at(-trailing - 1))) {
    trailing++;
  }
  let j = i;
  let breaks = 0;
  while (j < s.length && s[j] === "\n") {
    breaks++;
    j = skipInlineSpace(s, j + 1);
  }
  return {
    add: breaks === 1 ? " " : "\n".repeat(breaks - 1),
    trim: trailing,
    next: j,
  };
}

function readHexEscape(
  s: string,
  i: number,
  kind: string,
  fail: Fail
): { text: string; next: number } {
  const length = HEX_LENGTHS[kind];
  const hex = s.slice(i + 2, i + 2 + length);
  const code = Number.parseInt(hex, 16);
  if (hex.length !== length || !RE_HEX_ESCAPE.test(hex) || code > 0x10_ff_ff) {
    return fail("badEscape", i, { char: kind });
  }
  return { text: String.fromCodePoint(code), next: i + 2 + length };
}

/** One backslash escape at s[i]: the text it stands for and where reading resumes. */
function readEscape(
  s: string,
  i: number,
  fail: Fail
): { text: string; next: number } {
  const e = s[i + 1];
  if (e === "\n") {
    // An escaped line break joins the lines with nothing between them.
    return { text: "", next: skipInlineSpace(s, i + 2) };
  }
  if (e === "x" || e === "u" || e === "U") {
    return readHexEscape(s, i, e, fail);
  }
  if (e !== undefined && Object.hasOwn(SIMPLE_ESCAPES, e)) {
    return { text: SIMPLE_ESCAPES[e], next: i + 2 };
  }
  return fail("badEscape", i, { char: e ?? "" });
}

/** Read a double-quoted scalar starting at s[start] === '"'. `null` when it never closes. */
export function readDoubleQuoted(
  s: string,
  start: number,
  fail: Fail
): Quoted | null {
  let out = "";
  let i = start + 1;
  while (i < s.length) {
    const c = s[i];
    if (c === '"') {
      return { value: out, end: i + 1 };
    }
    if (c === "\n") {
      const fold = foldBreak(s, i, out);
      out = out.slice(0, out.length - fold.trim) + fold.add;
      i = fold.next;
    } else if (c === "\\") {
      const decoded = readEscape(s, i, fail);
      out += decoded.text;
      i = decoded.next;
    } else {
      out += c;
      i++;
    }
  }
  return null;
}

/** Read a single-quoted scalar starting at s[start] === "'". `null` when it never closes. */
export function readSingleQuoted(s: string, start: number): Quoted | null {
  let out = "";
  let i = start + 1;
  while (i < s.length) {
    const c = s[i];
    if (c === "'") {
      if (s[i + 1] === "'") {
        out += "'";
        i += 2;
      } else {
        return { value: out, end: i + 1 };
      }
    } else if (c === "\n") {
      const fold = foldBreak(s, i, out);
      out = out.slice(0, out.length - fold.trim) + fold.add;
      i = fold.next;
    } else {
      out += c;
      i++;
    }
  }
  return null;
}

/** Does a quoted scalar that starts at s[start] close inside s? */
export function quotedEnd(s: string, start: number): number {
  const quote = s[start];
  let i = start + 1;
  while (i < s.length) {
    if (quote === '"' && s[i] === "\\") {
      i += 2;
    } else if (s[i] === quote) {
      if (quote === "'" && s[i + 1] === "'") {
        i += 2;
      } else {
        return i + 1;
      }
    } else {
      i++;
    }
  }
  return -1;
}

// ---------------------------------------------------------------------------
// Block scalars
// ---------------------------------------------------------------------------

export type Chomp = "strip" | "clip" | "keep";

export interface BlockHeader {
  folded: boolean;
  chomp: Chomp;
  /** The explicit indentation indicator, or 0 when the indentation is detected. */
  indent: number;
}

const RE_BLOCK_HEADER = /^([|>])([1-9]?)([-+]?)([1-9]?)(?:[ \t]+#.*|[ \t]*)$/;

/** Parse `|`, `>-`, `|2+`, `>+1` … with an optional trailing comment. */
export function parseBlockHeader(text: string): BlockHeader | null {
  const match = RE_BLOCK_HEADER.exec(text);
  if (!match) {
    return null;
  }
  const [, style, before, sign, after] = match;
  if (before !== "" && after !== "") {
    return null;
  }
  let chomp: Chomp = "clip";
  if (sign === "-") {
    chomp = "strip";
  } else if (sign === "+") {
    chomp = "keep";
  }
  return {
    folded: style === ">",
    chomp,
    indent: Number(before || after || "0"),
  };
}

/** Join the lines of a block scalar: literal keeps breaks, folded folds ordinary lines. */
export function assembleBlock(
  lines: string[],
  folded: boolean,
  chomp: Chomp
): string {
  let end = lines.length;
  while (end > 0 && lines[end - 1] === "") {
    end--;
  }
  const trailingBlank = lines.length - end;
  const body = folded
    ? foldLines(lines.slice(0, end))
    : lines.slice(0, end).join("\n");
  if (end === 0) {
    return chomp === "keep" ? "\n".repeat(trailingBlank) : "";
  }
  if (chomp === "strip") {
    return body;
  }
  return chomp === "clip"
    ? `${body}\n`
    : `${body}\n${"\n".repeat(trailingBlank)}`;
}

function isMoreIndented(line: string): boolean {
  return line.startsWith(" ") || line.startsWith("\t");
}

function foldLines(lines: string[]): string {
  let out = "";
  let blank = 0;
  let previousMore = false;
  let started = false;
  for (const line of lines) {
    if (line === "") {
      blank++;
      continue;
    }
    const more = isMoreIndented(line);
    if (!started) {
      out = "\n".repeat(blank) + line;
      started = true;
    } else if (more || previousMore) {
      out += "\n".repeat(blank + 1) + line;
    } else {
      out += (blank === 0 ? " " : "\n".repeat(blank)) + line;
    }
    previousMore = more;
    blank = 0;
  }
  return out;
}
